/* eslint-disable react/no-unescaped-entities */

import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { Quote, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "@/lib/content";function Avatar({ name, photo }) {
  const initials = name.split(" ").map((n) => n[0]).slice(0, 2).join("");
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        loading="lazy"
        className="w-14 h-14 object-cover border border-[#FFD60A]/40 shrink-0"
      />
    );
  }
  return (
    <div className="w-12 h-12 flex items-center justify-center bg-[#FFD60A]/15 border border-[#FFD60A]/40 font-display text-[#FFD60A] text-lg shrink-0">
      {initials}
    </div>
  );
}

export default function Testimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-[#080808] border-y border-zinc-900" id="temoignages" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-10 md:mb-12 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#FFD60A]" />
              <span className="font-mono text-xs tracking-[0.4em] text-[#FFD60A] uppercase">Témoignages</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95]">
              Ils ont <span className="text-[#FFD60A]">transformé</span> leur business
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={scrollPrev}
              className="w-12 h-12 border border-zinc-800 hover:border-[#FFD60A] hover:bg-[#FFD60A]/10 flex items-center justify-center transition-all text-white"
              data-testid="testimonials-prev"
              aria-label="Précédent"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              onClick={scrollNext}
              className="w-12 h-12 border border-zinc-800 hover:border-[#FFD60A] hover:bg-[#FFD60A]/10 flex items-center justify-center transition-all text-white"
              data-testid="testimonials-next"
              aria-label="Suivant"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        {/* Mobile counter + arrows */}
        <div className="flex items-center justify-between mb-4 md:hidden">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#FFD60A]">
            {String(selectedIndex + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={scrollPrev}
              className="w-9 h-9 border border-zinc-800 hover:border-[#FFD60A] hover:bg-[#FFD60A]/10 flex items-center justify-center transition-all text-white"
              aria-label="Précédent"
              data-testid="testimonials-mobile-prev"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={scrollNext}
              className="w-9 h-9 border border-zinc-800 hover:border-[#FFD60A] hover:bg-[#FFD60A]/10 flex items-center justify-center transition-all text-white"
              aria-label="Suivant"
              data-testid="testimonials-mobile-next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-4 md:gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="shrink-0 w-[88%] sm:w-[80%] md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] bg-[#0a0a0a] border border-zinc-800 p-6 md:p-8 flex flex-col"
                data-testid={`testimonial-card-${i}`}
              >
                <Quote size={28} className="text-[#FFD60A] mb-5 md:mb-6" />
                <p className="text-zinc-200 leading-relaxed flex-1 italic text-sm md:text-base">"{t.quote}"</p>
                <div className="mt-6 pt-6 border-t border-zinc-800 flex items-center gap-4">
                  <Avatar name={t.name} photo={t.photo} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-white uppercase tracking-wide leading-none">{t.name}</div>
                    <div className="text-xs text-zinc-500 mt-1">{t.role}</div>
                  </div>
                </div>
                <div className="mt-4 inline-flex self-start px-3 py-1 bg-[#FFD60A]/10 border border-[#FFD60A]/30 font-mono text-[10px] text-[#FFD60A] uppercase tracking-widest">
                  {t.stat}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Dot indicators (mobile + small desktop) */}
        <div className="mt-6 flex items-center justify-center gap-2 md:hidden">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              className={`h-1 transition-all ${
                i === selectedIndex ? "w-8 bg-[#FFD60A]" : "w-3 bg-zinc-700 hover:bg-zinc-500"
              }`}
              aria-label={`Témoignage ${i + 1}`}
              data-testid={`testimonials-dot-${i}`}
            />
          ))}
        </div>

        <div className="mt-4 text-center md:hidden">
          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
            ← Glissez pour voir tous les avis →
          </span>
        </div>
      </div>
    </section>
  );
}
